"use client";

import React, {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useMemo,
  useOptimistic,
  useState,
} from "react";

/**
 * 楽観的更新のアクション型
 * メモリ効率化のため、IDの順序のみを保持
 */
type OptimisticAction = {
  projectId: string;
  viewIds: string[];
};

/**
 * Context の型定義
 * ビューのIDの順序のみを管理し、実体データはコンポーネント側で管理
 * SA, UA, IWなど全プロジェクトタイプで使用可能
 */
interface ViewOrderContextType {
  // ビューIDの並び順を取得（楽観的な状態）
  getViewOrder: (projectId: string) => string[] | undefined;

  // ビューIDの並び順を設定（サーバー成功後）
  setViewOrder: (projectId: string, viewIds: string[]) => void;

  // 楽観的更新（即座にUIを更新）
  updateViewOrder: (projectId: string, viewIds: string[]) => void;
}

/**
 * Context の作成
 */
const ViewOrderContext = createContext<ViewOrderContextType | undefined>(
  undefined,
);

/**
 * Provider コンポーネント
 */
export function ViewOrderProvider({ children }: { children: React.ReactNode }) {
  // 実際の状態（サーバーと同期）- IDの順序のみを保持
  const [viewOrders, setViewOrders] = useState<Record<string, string[]>>({});

  // useOptimisticで楽観的な状態を管理
  const [optimisticViewOrders, addOptimisticUpdate] = useOptimistic(
    viewOrders,
    (currentState, action: OptimisticAction) => ({
      ...currentState,
      [action.projectId]: action.viewIds,
    }),
  );

  // ビューIDの並び順を取得（楽観的な状態から）
  const getViewOrder = useCallback(
    (projectId: string) => {
      return optimisticViewOrders[projectId];
    },
    [optimisticViewOrders],
  );

  // ビューIDの並び順を設定（サーバー成功後）
  const setViewOrder = useCallback((projectId: string, viewIds: string[]) => {
    setViewOrders((prev) => ({
      ...prev,
      [projectId]: viewIds,
    }));
  }, []);

  // 楽観的更新（即座にUIを更新）
  const updateViewOrder = useCallback(
    (projectId: string, viewIds: string[]) => {
      startTransition(() => {
        addOptimisticUpdate({ projectId, viewIds });
      });
    },
    [addOptimisticUpdate],
  );

  // Context の値をメモ化
  const value = useMemo(
    () => ({
      getViewOrder,
      setViewOrder,
      updateViewOrder,
    }),
    [getViewOrder, setViewOrder, updateViewOrder],
  );

  return (
    <ViewOrderContext.Provider value={value}>
      {children}
    </ViewOrderContext.Provider>
  );
}

/**
 * Context を使用するためのカスタムフック
 * すべてのプロジェクトタイプ（SA, UA, IW）で共通して使用
 */
export function useViewOrder() {
  const context = useContext(ViewOrderContext);
  if (context === undefined) {
    throw new Error("useViewOrder must be used within a ViewOrderProvider");
  }
  return context;
}
